import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { Complaint } from "../../../logic/api";
import { useDate } from "../../../utility/useDate";
import { SessionContext } from "../../../utility/useSession";
import { getFileUrl } from "../../../utility/utils";
import styles from "./styles";
import { type ComplaintImage } from "./BookingComplaint";

interface Row {
    header: string,
    field: string,
}

export const PrintedForm: React.FunctionComponent<{
    complaint: Complaint,
    complaintType: string,
    selectedImages?: ComplaintImage[]
}> = (props) => {

    const session = useContext(SessionContext);
    const { t } = useTranslation();

    const { localizeDate, toFormattedFullDateTimeString } = useDate();
    const { complaint, complaintType } = props;

    const ComplaintTitle: React.FunctionComponent = () => {
        return (<div style={styles.printedFormTitle}>
            <div style={styles.printedFormComplaintType}>{complaintType}</div>
        </div>);
    }

    const CompanyLogo: React.FunctionComponent = () => {
        return (<div style={styles.printedFormCompanyLogo}>
            {session.current.site?.preferences.company_logo_base64 ?
                <img style={styles.printedFormCompanyLogo} src={`data:image/jpeg;base64, ${session.current.site?.preferences.company_logo_base64}`} />
                : <div>{session.current.site?.preferences.company_name}</div>}
        </div>);
    }

    const Divider: React.FunctionComponent<{ header: string }> = (props) => {
        const { header } = props;

        return (<span style={styles.printedFormDivider}>
            <div style={styles.printedFormDividerLineLeft}></div><div style={styles.printedFormSectionTitle}>{header}</div><div style={styles.printedFormDividerLineRight}></div>
        </span>);
    }

    const AdminField: React.FunctionComponent<Row> = (props: Row) => {
        const { header, field } = props;

        return (<span style={styles.printedFormAdminRow}>
            <div style={styles.printedFormAdminHeader}>{header}</div><div style={styles.printedFormAdminField}>{field}</div>
        </span>);
    }

    const FormField: React.FunctionComponent<Row> = (props: Row) => {
        const { header, field } = props;

        return (<div style={styles.printedFormRow}>
            <div style={styles.printedFormFieldHeader}>{header}</div>
            <div style={styles.printedFormField}>{field?.startsWith("http") ?
                (<img src={field} />)
                : field}
            </div>
        </div>);
    }

    const AdminSection: React.FunctionComponent = () => {
        return (<div style={styles.printedFormSection}>
            <div style={styles.printedFormColumn}>
                <AdminField header={"Created:"} field={toFormattedFullDateTimeString(complaint.complaintDate)} />
                <AdminField header={"Logged By:"} field={complaint.createdBy.firstName} />
                <AdminField header={"Device:"} field={complaint.deviceSerialNo} />
            </div>
            <div style={styles.printedFormColumn}>
                <AdminField header={"Booking:"} field={complaint.booking.ref} />
                <AdminField header={"Vehicle:"} field={`${complaint.booking.vehicle.make || ""} ${complaint.booking.vehicle.model || ""} (${complaint.booking.vehicle.colour})`} />
                <AdminField header={"Registration:"} field={complaint.booking.vehicle.registration} />
            </div>
        </div>);
    }

    const CustomerSection: React.FunctionComponent = () => {
        return (<div>
            <Divider header={"Customer"} />
            <div style={styles.printedFormSection}>
                <div style={styles.printedFormColumn}>
                    <FormField header={"Customer Name"} field={complaint.leadPersonName} />
                    <FormField header={"Email"} field={complaint.email} />
                    <FormField header={"Address Line 1"} field={complaint.address1} />
                    <FormField header={"Address Line 2"} field={complaint.address2} />
                </div>
                <div style={styles.printedFormColumn}>
                    <FormField header={"Phone (Work)"} field={complaint.phoneWork} />
                    <FormField header={"Phone (Mobile)"} field={complaint.phoneMobile} />
                    <FormField header={"Town/City"} field={complaint.townCity} />
                    <FormField header={"County"} field={complaint.county} />
                    <FormField header={"Postcode"} field={complaint.postCode} />
                </div>
            </div>
        </div>);
    }

    const DetailsSection: React.FunctionComponent = () => {
        return (<div>
            <Divider header={"Details"} />
            <div style={styles.printedFormDetailsSection}>
                <FormField header={"Complaint Details"} field={complaint.complaintDetails || "none"} />
                <FormField header={"Verbal Comments"} field={complaint.verbalComments || "none"} />
                <FormField header={"Staff Comments"} field={complaint.staffComments || "none"} />
            </div>
        </div>);
    }

    const QuestionsSection: React.FunctionComponent = () => {
        return (<div style={{ "pageBreakInside": "avoid" }}>
            <Divider header={"Questions"} />
            <div style={styles.printedFormSection}>
                <div style={styles.printedFormColumn}>
                    <FormField header={"Damage looks fresh?"} field={complaint.damageLooksFresh} />
                    <FormField header={"Any other damage adjacent?"} field={complaint.damageAdjacent} />
                </div>
                <div style={styles.printedFormColumn}>
                    <FormField header={"Rust around damaged area?"} field={complaint.rustAroundDamage} />
                    <FormField header={"Damage obvious?"} field={complaint.damageObvious} />
                </div>
                <div style={styles.printedFormColumn}>
                    <FormField header={"Damage happened in car park?"} field={complaint.damageHappendInCarPark} />
                    <FormField header={"General condition?"} field={complaint.generalCondition} />
                </div>
            </div>
        </div>);
    }

    const SignaturesSection: React.FunctionComponent = () => {
        return (<div style={{ "pageBreakInside": "avoid" }}>
            <Divider header={"Signatures"} />
            <div style={styles.printedFormSection}>
                <div style={styles.printedFormColumn}>
                    <FormField header={"Customer Signature"} field={complaint.signatureFile ? getFileUrl(session, complaint.signatureFile) : ""} />
                </div>
                <div style={styles.printedFormColumn}>
                    <FormField header={"Date"} field={toFormattedFullDateTimeString(complaint.signatureFile ? complaint.complaintDate : "")} />
                </div>
            </div>
        </div>)
    }

    const ImagesSection: React.FunctionComponent = () => {
        if (!props.selectedImages || props.selectedImages.length === 0) {
            return null;
        }

        return (<div style={{ "pageBreakInside": "avoid" }}>
            <Divider header={t("complaints.images.title")} />
            <div style={styles.printedFormSection}>
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%"
                }}>
                    {props.selectedImages.map((image) => (
                        <div key={image.clientId} style={{
                            textAlign: "left",
                            pageBreakInside: "avoid",
                            width: "100%"
                        }}>
                            <img
                                src={image.url}
                                style={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    maxHeight: "400px",
                                    objectFit: "scale-down",
                                    display: "block"
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>);
    }

    const Footer: React.FunctionComponent = () => {
        return (<div style={{ paddingTop: 80 }}>
            <div style={styles.printedFormFooter1}>{
                session.current.site?.preferences.company_website
            }, {
                    session.current.site?.preferences.company_email
                }, {
                    session.current.site?.preferences.company_telephone
                }</div>
            <div style={styles.printedFormFooter2}>Printed by {
                session.current.site?.user.firstName
            } {
                    session.current.site?.user.lastName
                } on {
                    toFormattedFullDateTimeString(localizeDate(new Date()).toString())
                }. {
                    session.current.site?.preferences.company_name
                } - {
                    complaint.carParkName
                }.</div>
        </div>);
    }

    // Mounting the components in a table is the only way to have a persistent header and footer that doesn't interfere with other formatting

    return (<div>
        <table style={{ width: "100%" }}>
            <thead><tr><td align="right"><CompanyLogo /></td></tr></thead>
            <tbody>
                <tr><td><ComplaintTitle /></td></tr>
                <tr><td><AdminSection /></td></tr>
                <tr><td><CustomerSection /></td></tr>
                <tr><td><DetailsSection /></td></tr>
                <tr><td><QuestionsSection /></td></tr>
                <tr><td><SignaturesSection /></td></tr>
                <tr><td><ImagesSection /></td></tr>
            </tbody>
            <tfoot><tr><td><Footer /></td></tr></tfoot>
        </table>
    </div>)
}